import React, {useContext, useEffect, useMemo, useState} from "react";
import {toast} from "react-toastify";
import LoadingScreen from "../components/loading-screen/LoadingScreen";
import {db} from "../config/firebase";
import {doc, getDoc, setDoc, updateDoc} from "firebase/firestore";
import {v4 as uuid} from "uuid";
import {SignInContext} from "./SignInContext";

export const TOKEN = "token"

export const WebSettingsContext = React.createContext({
  settings: null,
  searchParams: {
    organization: null,
    term: "",
  },
  onUpdateParams: () => {},
  onUnderMaintenance: () => {},
  onSaveModel: async () => {},
  onDeleteModel: async () => {},
  modelIds: [],
})

const id = 'blockflow';

export const WebSettingsContextProvider = props => {
  const {organizations} = useContext(SignInContext);
  const [settings, setSettings] = useState(null);
  const [searchParams, setSearchParams] = useState({
    organization: null, term: ""
  });

  const modelIds = useMemo(() => {
    const allModels = settings?.models || [];
    const userOrgIds = (organizations || []).map(o => o.id);
    const filtered = allModels.filter(m =>
      !m.organizationId || userOrgIds.includes(m.organizationId)
    );
    return [...new Set(filtered.map(m => m.modelId))];
  }, [settings, organizations]);

  useEffect(() => {
    const fetchData = async () => {
      const docRef = doc(db, "web_settings", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setSettings(docSnap.data());
      } else {
        const data = {
          underMaintenance: false,
          models: [],
          audios: []
        }
        await setDoc(doc(db, "web_settings", id), data);
        setSettings(data);
      }
    }

    fetchData()
  }, [])

  const onUnderMaintenance = async () => {
    try {
      const data = {
        underMaintenance: !settings.underMaintenance
      }
      await updateDoc(doc(db, "web_settings", id), data);
      setSettings(prev => ({...prev, ...data}));
      toast.success("Successfully updated settings")
    } catch (e) {
      console.error(e);
      toast.error(e.errorMessage)
    }
  }

  const onSaveModel = async (model) => {
    try {
      const currentModels = settings?.models || [];
      let updatedModels;
      if (model.id) {
        updatedModels = currentModels.map(m => m.id === model.id ? model : m);
      } else {
        const isDuplicate = currentModels.some(m => m.modelId === model.modelId);
        if (isDuplicate) {
          toast.error("A model with this ID already exists");
          return false;
        }
        updatedModels = [...currentModels, {...model, id: uuid()}];
      }
      await updateDoc(doc(db, "web_settings", id), {models: updatedModels});
      setSettings(prev => ({...prev, models: updatedModels}));
      toast.success("Model saved successfully");
      return true;
    } catch (e) {
      console.error(e);
      toast.error("Failed to save model");
      return false;
    }
  }

  const onDeleteModel = async (modelId) => {
    try {
      const updatedModels = (settings?.models || []).filter(m => m.id !== modelId);
      await updateDoc(doc(db, "web_settings", id), {models: updatedModels});
      setSettings(prev => ({...prev, models: updatedModels}));
      toast.success("Model deleted successfully");
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete model");
    }
  }

  const onUpdateParams = (data) => {
    setSearchParams(prev => ({...prev, ...data}))
  }

  return (
    <WebSettingsContext.Provider
      value={{
        settings: settings,
        modelIds: modelIds,
        searchParams: searchParams, onUpdateParams: onUpdateParams,
        onUnderMaintenance: onUnderMaintenance,
        onSaveModel: onSaveModel,
        onDeleteModel: onDeleteModel,
      }}
    >
      {settings === null ? <LoadingScreen/> : props.children}
    </WebSettingsContext.Provider>
  )
}